import {Data} from 'charts/BarChart/data';
import 'charts/BarChart/style.scss';
import * as d3 from 'd3';
import {ClassNameSelectorSpec, ensureSingleElem, selectAndJoin, SelectorSpec} from 'utils/d3';
import {Configurator} from 'utils/makeConfigurator';
import {deepMerge, flat1} from 'utils/object';

export const selectors = {
  rects: new ClassNameSelectorSpec('rect', 'bar-rect'),
  xAxisG: new ClassNameSelectorSpec('g', 'x-axis'),
  yAxisG: new ClassNameSelectorSpec('g', 'y-axis'),
  xAxisLabels: new SelectorSpec('text', 'g.x-axis g.tick text'),
  yAxisLabels: new SelectorSpec('text', 'g.y-axis g.tick text'),
  yAxisTickLines: new SelectorSpec('line', 'g.y-axis g.tick line'),
  yAxisTitle: new ClassNameSelectorSpec('text', 'y-axis-title'),
};

export default function update(
  rootElem: SVGSVGElement | SVGGElement,
  inData: Data,
  conf: Configurator,
) {
  const width = conf('width', 790);
  const height = conf('height', 430);

  const margin = {
    top: conf('margin.top', 40),
    left: conf('margin.left', 40),
    bottom: conf('margin.bottom', 21),
    right: conf('margin.right', 1),
  };

  let maxStackTotalValue = 0;
  const data = deepMerge(inData, {
    stacks: inData.stacks.map(({rects}, stackNo) => {
      const totalValue = d3.sum(rects, d => d.value);
      maxStackTotalValue = Math.max(maxStackTotalValue, totalValue);
      let accumVal = 0;
      return {
        totalValue,
        stackNo,
        // eslint-disable-next-line no-return-assign
        rects: rects.map(({value}) => ({
          // relVal: value / totalValue,
          accumValBefore: accumVal,
          accumValAfter: accumVal += value,
          stackNo,
        })),
      };
    }),
  });

  const allRects = flat1(data.stacks.map(d => d.rects));

  const x = d3
    .scaleBand(
      data.stacks.map(d => d.stackNo),
      [margin.left, width - margin.right],
    )
    .paddingInner(conf('scaleBand.paddingInner', 0.5))
    .paddingOuter(conf('scaleBand.paddingOuter', 1));

  const y = d3
    .scaleLinear()
    .domain([0, maxStackTotalValue])
    .range([height - margin.bottom, margin.top]);

  const xAxis = d3
    .axisBottom(x)
    .tickFormat(stackNo => data.stacks[stackNo].label)
    .tickSizeInner(conf('xAxis.tickSizeInner', 0))
    .tickSizeOuter(conf('xAxis.tickSizeOuter', 0))
    .tickPadding(conf('xAxis.tickPadding', conf('xAxis.tickPadding', 6)))
    .offset(conf('xAxis.offset', 0));

  const yAxis = d3
    .axisLeft(y)
    .tickFormat(count => Math.round(Number(count)).toString())
    .tickSizeInner(conf('yAxis.tickSizeInner', 0))
    .tickSizeOuter(conf('yAxis.tickSizeOuter', 0))
    .tickPadding(conf('yAxis.tickPadding', conf('yAxis.tickPadding', 4)))
    .offset(conf('yAxis.offset', 0))
    .ticks(conf('yAxis.ticks', conf('yAxis.ticks', 4)));

  const rootSelection = d3.select(rootElem).classed(conf('rootClass', 'BarChart-default'), true);

  if (rootSelection.node()?.nodeName === 'svg') {
    rootSelection.attr('viewBox', `0 0 ${width} ${height}`);
  }

  ensureSingleElem(rootSelection, selectors.yAxisG)
    .attr('transform', `translate(${margin.left},0)`)
    .call(yAxis)
    .call(g => g.select('.domain').remove())
    .call(g => g.selectAll('.tick line').attr('x2', width - margin.left - margin.right))
    .call(g => g.selectAll('.tick:first-of-type line').remove());

  selectAndJoin(rootSelection, selectors.rects, allRects)
    .on('mouseenter', (e, d) => d.events?.onMouseEnter(e))
    .on('mouseleave', (e, d) => d.events?.onMouseLeave(e))
    .attr('fill', d => d.color)
    .attr('x', d => x(d.stackNo) as number)
    .attr('y', d => y(d.accumValAfter))
    .attr('height', d => y(d.accumValBefore) - y(d.accumValAfter))
    .attr('width', x.bandwidth());

  ensureSingleElem(rootSelection, selectors.xAxisG)
    .attr('transform', `translate(0,${height - margin.bottom + 0.5})`)
    .call(xAxis);

  ensureSingleElem(rootSelection, selectors.yAxisTitle).text(
    conf('yAxisTitle.text', 'Количество объектов'),
  );
}
